import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import imageCompression from "browser-image-compression";
import {
  useItemFinishListener,
  useItemProgressListener,
  useItemStartListener,
  useUploady,
  useUploadyContext,
} from "@rpldy/uploady";
import loadImage from "blueimp-load-image";
import { useParams } from "react-router-dom";
import {
  clearGallery,
  customFetchImages,
  getFolderDetails,
  getGalleryImages,
  openMinimizedBox,
  setImagesLoading,
  uploadModalAction,
} from "../../store/slices/Gallery/gallerySlice";
import { handleIsUploading } from "../../store/slices/Gallery/fileUploadSlice";

let totalUploadingCount = 0;

const useFileUpload = () => {
  const uploady = useUploady();
  const dispatch = useDispatch();
  const { folderID } = useParams();
  const fileInputRef = useRef();
  const { abort, upload } = useUploadyContext();
  const [uploadReadyImages, setUploadReadyImages] = useState([]);
  const [currentlyUploading, setCurrenlyUploading] = useState();

  const { sections, currentTab, openMiniBox } = useSelector(
    (state) => state.gallery
  );

  const [validImages, setValidImages] = useState([]);
  const [uploadWarning, setUploadWarning] = useState(false);
  const [isQuited, setIsQuited] = useState(false);
  const fileUploadState = useSelector((state) => state.fileUpload);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadResponses, setUploadResponses] = useState({
    uploaded: 0,
    total: 0,
    uploadedImage: [],
  });

  const getAlbumsItems = async (obj) => {
    const resultAction = await dispatch(getGalleryImages(obj));
    dispatch(setImagesLoading(true));
    if (getGalleryImages.fulfilled.match(resultAction)) {
      loadMoreData(resultAction?.payload?.data?.result);
    }
  };

  const loadMoreData = async (Albums) => {
    if (Albums?.current_page < Albums.last_page) {
      const obj = {
        url: "/album/album-images",
        id: folderID,
        page: Albums?.current_page + 1,
        sort: "desc",
        per_page: 20,
      };
      const resultAction = await dispatch(customFetchImages(obj));
      if (customFetchImages.fulfilled.match(resultAction)) {
        loadMoreData(resultAction?.payload?.data?.result);
      }
    } else if (Albums?.current_page == Albums.last_page) {
      dispatch(setImagesLoading(false));
    }
  };
  useEffect(() => {
    async function fetchData() {
      await handleImageUpload(validImages[uploadResponses?.uploaded]);
    }
    if (
      uploadResponses?.total - uploadReadyImages?.length >=
        uploadResponses?.uploaded &&
      uploadResponses?.total != 0
    ) {
      fetchData();
    }
    if (
      uploadResponses?.total - uploadReadyImages?.length ==
      uploadResponses?.uploaded
    ) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }

    if (
      uploadResponses?.total - uploadReadyImages?.length ==
        uploadResponses?.uploaded &&
      uploadResponses?.total !== 0
    ) {
      dispatch(handleIsUploading(false));
      const obj = {
        url: "/album/album-images",
        id: folderID,
        page: 1,
        sort: "desc",
        per_page: 20,
      };
      getAlbumsItems(obj);
    }
  }, [validImages, uploadResponses]);

  const handleDragOver = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.stopPropagation();
    e.preventDefault();
    let imgArry = [...e.dataTransfer.files];
    handleFiles(imgArry);
  };
  const handleDragOut = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleImageChange = (e) => {
    handleFiles(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleFiles = (images) => {
    let files = [...images];
    totalUploadingCount = totalUploadingCount + files?.length;

    [...files]?.forEach((file) => {
      initializeUpload(file);
    });

    setUploadResponses((prev) => {
      return { ...prev, total: totalUploadingCount };
    });
  };

  const initializeUpload = async (image) => {
    if (checkDuplicate(image)) {
      setUploadReadyImages((prevState) => [
        ...prevState,
        { image, duplicate: true },
      ]);
    } else if (!checkExtension(image)) {
      setUploadReadyImages((prevState) => [
        ...prevState,
        { image, invalid: true },
      ]);
    } else {
      setValidImages((prevState) => [...prevState, image]);
    }
  };

  // uploadResponses?.total - uploadReadyImages?.length

  const checkDuplicate = (image) => {
    const allUploadedCount =
      [...sections?.all?.data, ...uploadResponses?.uploadedImage] || [];
    return allUploadedCount.some((obj) => obj.original_name === image.name);
  };

  const checkExtension = (image) => {
    const allowedExtensions =
      /(\.jpg|\.jpeg|\.png|\.gif|\.svg|\.avif|\.heic)$/i;
    return allowedExtensions.test(image.name);
  };
  // useItemProgressListener((item) => {
  //   setIsUploading(true);
  // });
  useItemStartListener((item) => {
    if (openMiniBox) {
      dispatch(handleIsUploading(true));
    }
    setCurrenlyUploading(item);
    if (isQuited) {
      abort(item.id);
    }
  });

  useItemFinishListener((item) => {
    if (fileUploadState?.uploadMode === "photoFind") {
      if (item.uploadResponse?.data?.result && !isQuited) {
        setUploadResponses((prev) => {
          return {
            uploaded: prev?.uploaded + 1,
            total: prev?.total,
            uploadedImage: [
              ...prev?.uploadedImage,
              {
                src: item.uploadResponse?.data?.result?.image_info?.src
                  ?.thumbnail_small,
                name: item.uploadResponse?.data?.result?.image_info?.file_name,
                orginal_name:
                  item.uploadResponse?.data?.result?.image_info
                    ?.thumbnail_original,
              },
            ],
          };
        });
      }
    }
  });

  const handleImageUpload = async (imageFile) => {
    const options = {
      maxSizeMB: 3,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(imageFile, options);

      loadImage.parseMetaData(imageFile, (data) => {
        const params = {
          album_id: folderID,
          image_name: compressedFile?.name,
        };

        if (data?.exif) {
          params.exif = JSON.stringify(data?.exif?.getAll());
        }
        upload(compressedFile, {
          destination: {
            params,
            filesParamName: "image",
          },
        });
      });

      return true;
    } catch (error) {
      uploady.upload(imageFile, {
        destination: {
          params: { album_id: folderID, image_name: imageFile?.name },
          filesParamName: "image",
        },
      });
      return true;
    }
  };

  const closeModal = async () => {
    dispatch(uploadModalAction(false));
    if (
      uploadResponses?.total - uploadReadyImages?.length >
        uploadResponses?.uploaded &&
      uploadResponses?.total != 0
    ) {
      let obj = {
        id: folderID,
        url: "/album/get-album",
      };
      await dispatch(getFolderDetails(obj)).then((r) => {
        setUploadWarning(false);
      });
      let data = {
        url: "/album/album-images",
        id: folderID,
        page: 1,
        sort: "desc",
        per_page: 20,
      };
      // await dispatch(clearGallery());
      await dispatch(getGalleryImages(data));
      dispatch(openMinimizedBox(true));
      dispatch(handleIsUploading(true));
    } else {
      const itemData = {
        url: "/album/album-images",
        id: folderID,
        page: 1,
        sort: "desc",
        per_page: 20,
      };
      getAlbumsItems(itemData);
      let obj = {
        id: folderID,
        url: "/album/get-album",
      };
      await dispatch(getFolderDetails(obj)).then((r) => {
        setUploadWarning(false);
      });
      let data = {
        url: "/album/album-images",
        id: folderID,
        page: 1,
        sort: "desc",
        per_page: 20,
      };

      // await dispatch(clearGallery());
      await dispatch(getGalleryImages(data));
      setUploadReadyImages([]);
      setCurrenlyUploading([]);
      setUploadResponses({
        uploaded: 0,
        total: 0,
        uploadedImage: [],
      });
      totalUploadingCount = 0;
      setValidImages([]);
      dispatch(setImagesLoading(false));
    }
    dispatch(setImagesLoading(false));
  };

  const handleCloseMiniMizedModal = () => {
    if (
      uploadResponses?.total - uploadReadyImages?.length >
        uploadResponses?.uploaded &&
      uploadResponses?.total != 0
    ) {
      setUploadWarning(true);
    } else {
      const itemData = {
        url: "/album/album-images",
        id: folderID,
        page: 1,
        sort: "desc",
        per_page: 20,
      };
      getAlbumsItems(itemData);
      dispatch(openMinimizedBox(false));
      setUploadWarning(false);
      closeModal();
    }
  };

  const handleQuitUploading = async () => {
    setIsQuited(true);
    closeModal();
    setUploadReadyImages([]);
    setCurrenlyUploading([]);
    setValidImages([]);

    let obj = {
      id: folderID,
      url: "/album/get-album",
    };
    await dispatch(getFolderDetails(obj)).then((r) => {
      setUploadWarning(false);
    });
    const itemData = {
      url: "/album/album-images",
      id: folderID,
      page: 1,
      sort: "desc",
      per_page: 20,
    };
    getAlbumsItems(itemData);
    let data = {
      url: "/album/album-images",
      id: folderID,
      page: 1,
      sort: "desc",
      per_page: 20,
    };
    await dispatch(clearGallery());
    await dispatch(getGalleryImages(data));
    setUploadResponses({
      uploaded: 0,
      total: 0,
      uploadedImage: [],
    });
    dispatch(openMinimizedBox(false));
    dispatch(handleIsUploading(false));
    setUploadWarning(false);
  };

  return {
    handleImageChange,
    handleDrop,
    uploadReadyImages,
    // uploadedCount,
    validImages,
    closeModal,
    handleCloseMiniMizedModal,
    uploadWarning,
    setUploadWarning,
    handleQuitUploading,
    handleDragOver,
    handleDragOut,
    uploadResponses,
    currentlyUploading,
    fileInputRef,
    fileUploadState,
  };
};

export default useFileUpload;
